import React, { useState, useEffect, useRef } from 'react';
import { Cloud, Mail, Github, AlertTriangle, Check, Loader, ExternalLink, Copy, X, Info } from 'lucide-react';

// Integration icons mapping
const INTEGRATION_ICONS = {
  aws: Cloud,
  google_workspace: Mail,
  github: Github,
};

// Integration display names
const INTEGRATION_NAMES = {
  aws: 'Amazon Web Services',
  google_workspace: 'Google Workspace',
  github: 'GitHub',
};

// Integration descriptions
const INTEGRATION_DESCRIPTIONS = {
  aws: 'Monitor IAM policies, S3 bucket security, and infrastructure configuration',
  google_workspace: 'Verify user access controls, admin settings, and workspace security',
  github: 'Check repository security, branch protection, and code scanning settings',
};

// Integration colors
const INTEGRATION_COLORS = {
  aws: 'bg-orange-500',
  google_workspace: 'bg-blue-500',
  github: 'bg-gray-900',
};

// AWS SSO Configuration Modal
const AWSSSOModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [ssoStartUrl, setSsoStartUrl] = useState('');
  const [ssoRegion, setSsoRegion] = useState('us-east-1');
  const [integrationName, setIntegrationName] = useState('');
  const [error, setError] = useState('');

  const AWS_REGIONS = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1',
    'ap-northeast-1', 'ap-southeast-1', 'ap-southeast-2',
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!ssoStartUrl.trim()) {
      setError('SSO Start URL is required');
      return;
    }

    if (!ssoStartUrl.includes('.awsapps.com/start')) {
      setError('Invalid SSO Start URL. It should look like: https://your-company.awsapps.com/start');
      return;
    }

    if (!integrationName.trim()) {
      setError('Integration name is required');
      return;
    }

    onSubmit({
      name: integrationName.trim(),
      ssoStartUrl: ssoStartUrl.trim(),
      ssoRegion,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Connect AWS via SSO</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Integration Name
            </label>
            <input
              type="text"
              value={integrationName}
              onChange={(e) => setIntegrationName(e.target.value)}
              placeholder="e.g., Production AWS Account"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AWS SSO Start URL
            </label>
            <input
              type="url"
              value={ssoStartUrl}
              onChange={(e) => setSsoStartUrl(e.target.value)}
              placeholder="https://your-company.awsapps.com/start"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Find this in your AWS IAM Identity Center settings
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AWS Region
            </label>
            <select
              value={ssoRegion}
              onChange={(e) => setSsoRegion(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              disabled={isLoading}
            >
              {AWS_REGIONS.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// AWS SSO Verification Modal (shows the verification URL)
const AWSSSOVerificationModal = ({
  isOpen,
  verificationUrl,
  userCode,
  onCancel,
  status,
  message,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(userCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Cloud className="w-8 h-8 text-orange-600" />
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Complete AWS SSO Authentication
          </h3>

          <p className="text-sm text-gray-600 mb-4">
            Click the link below to authenticate with AWS IAM Identity Center.
            You may need to enter the code shown.
          </p>

          {userCode && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-1">Your code:</p>
              <div className="flex items-center justify-center gap-2">
                <code className="px-4 py-2 bg-gray-100 rounded-lg text-lg font-mono font-bold text-gray-900">
                  {userCode}
                </code>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Copy code"
                >
                  {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          <a
            href={verificationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 mb-4"
          >
            Open AWS Login
            <ExternalLink className="w-4 h-4" />
          </a>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
            <Loader className="w-4 h-4 animate-spin" />
            <span>{message || 'Waiting for authentication...'}</span>
          </div>

          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// AWS SSO Role Selection Modal
const AWSSSOSelectRoleModal = ({
  isOpen,
  accounts,
  loading,
  error,
  message,
  onSelectRole,
  onCancel,
}) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [selecting, setSelecting] = useState(false);

  if (!isOpen) return null;

  const handleSelect = async () => {
    if (!selectedAccount || !selectedRole) return;
    setSelecting(true);
    try {
      await onSelectRole(selectedAccount.account_id, selectedRole);
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Select AWS Account & Role</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
            disabled={selecting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 text-teal-600 animate-spin" />
              <span className="ml-2 text-gray-600">Loading accounts...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          ) : accounts.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              No AWS accounts found. Make sure you have access to at least one account via IAM Identity Center.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Select an AWS account and role to use for compliance scanning.
              </p>

              {/* Account Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AWS Account
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {accounts.map((account) => (
                    <button
                      key={account.account_id}
                      onClick={() => {
                        setSelectedAccount(account);
                        setSelectedRole('');
                      }}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedAccount?.account_id === account.account_id
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">
                        {account.account_name || account.account_id}
                      </div>
                      <div className="text-xs text-gray-500">
                        {account.account_id}
                        {account.email_address && ` • ${account.email_address}`}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Role Selection */}
              {selectedAccount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IAM Role
                  </label>
                  {selectedAccount.roles?.length > 0 ? (
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value="">Select a role...</option>
                      {selectedAccount.roles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                      No roles available for this account.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {message && !loading && (
            <div className="mt-4 text-sm text-gray-600 text-center">
              {message}
            </div>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            disabled={selecting}
          >
            Cancel
          </button>
          <button
            onClick={handleSelect}
            disabled={!selectedAccount || !selectedRole || selecting}
            className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {selecting ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Account'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const IntegrationCard = ({
  type, // 'aws' | 'google_workspace' | 'github'
  connected,
  status, // 'disconnected' | 'connecting' | 'connected' | 'error' | 'selecting_role'
  integrationName, // Custom name if set
  integrationId, // Integration UUID
  onConnect,
  onConnectSSO, // New: For AWS SSO connection
  onSkip,
  disabled = false,
  // SSO-specific props
  ssoState,
  ssoVerificationUrl,
  ssoUserCode,
  ssoStatus,
  ssoMessage,
  onCancelSSO,
  // SSO role selection props
  ssoShowRoleSelection,
  ssoAccounts,
  ssoAccountsLoading,
  ssoAccountsError,
  onSelectSSORole,
  onConfigureRole, // For configuring role on existing SSO integration
  // GitHub App specific props
  githubAppInfo,
  onRemoveInstallation,
}) => {
  const [showSSOModal, setShowSSOModal] = useState(false);
  const Icon = INTEGRATION_ICONS[type] || Cloud;
  const displayName = integrationName || INTEGRATION_NAMES[type];
  const description = INTEGRATION_DESCRIPTIONS[type];
  const iconBgColor = INTEGRATION_COLORS[type];

  const isConnecting = status === 'connecting';
  const hasError = status === 'error';
  const isAWS = type === 'aws';
  const isGitHub = type === 'github';

  // Show SSO verification modal when we have a verification URL
  const showVerificationModal = isAWS && ssoState && ssoVerificationUrl;

  // GitHub App info
  const githubAppConfigured = isGitHub && githubAppInfo?.configured;
  const githubInstallations = isGitHub ? (githubAppInfo?.installations || []) : [];
  const hasGitHubInstallations = githubInstallations.length > 0;

  // For GitHub, consider it "ready" if App is installed, even if no scan has been run yet
  const isGitHubReady = isGitHub && hasGitHubInstallations;
  const effectiveConnected = connected || isGitHubReady;

  const handleConnectClick = () => {
    if (isAWS && onConnectSSO) {
      // Show SSO configuration modal for AWS
      setShowSSOModal(true);
    } else {
      // Use default connect for other integrations (including GitHub which opens install page)
      onConnect();
    }
  };

  const handleSSOSubmit = (config) => {
    setShowSSOModal(false);
    if (onConnectSSO) {
      onConnectSSO(config);
    }
  };

  // Get connect button text based on integration type
  const getConnectButtonText = () => {
    if (isConnecting) return 'Connecting...';
    if (connected) return 'Reconnect';
    if (isGitHub) {
      if (hasGitHubInstallations) return 'Manage Installation';
      return 'Install GitHub App';
    }
    return 'Connect';
  };

  // Get status text for GitHub
  const getStatusText = () => {
    if (isConnecting) return 'Connecting...';
    if (hasError) return 'Connection failed';
    if (connected) return 'Connected';
    if (isGitHubReady) return 'Ready to Scan';
    return 'Not Connected';
  };

  // Get status color class
  const getStatusColor = () => {
    if (isConnecting) return 'text-teal-600';
    if (hasError) return 'text-red-600';
    if (effectiveConnected) return 'text-gray-600';
    return 'text-gray-600';
  };

  // Get status dot color
  const getStatusDotColor = () => {
    if (effectiveConnected) return 'bg-green-500';
    return 'bg-gray-400';
  };

  return (
    <>
      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-100">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4 min-w-0">
            {/* Icon */}
            <div className={`w-12 h-12 ${iconBgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-6 h-6 text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {displayName}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {description}
              </p>

              {/* Status Indicator */}
              <div className="flex flex-wrap items-center gap-2">
                {isConnecting ? (
                  <>
                    <Loader className="w-4 h-4 text-teal-600 animate-spin" />
                    <span className="text-sm text-teal-600">Connecting...</span>
                  </>
                ) : hasError ? (
                  <>
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-red-600">Connection failed</span>
                  </>
                ) : (
                  <>
                    <div className={`w-2 h-2 rounded-full ${getStatusDotColor()}`} />
                    <span className={`text-sm ${getStatusColor()}`}>{getStatusText()}</span>
                    {effectiveConnected && <Check className="w-4 h-4 text-green-500 ml-1" />}
                  </>
                )}
              </div>

              {/* SSO Badge for AWS */}
              {isAWS && !connected && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                    IAM Identity Center (SSO)
                  </span>
                </div>
              )}

              {/* GitHub App Badge and Instructions */}
              {isGitHub && (
                <div className="mt-2 space-y-2">
                  {hasGitHubInstallations ? (
                    // Show installed organizations
                    <div className="flex items-start gap-2 text-xs text-green-600">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        GitHub App installed on {githubInstallations.map(i => i.account?.login).join(', ')}
                      </span>
                    </div>
                  ) : githubAppConfigured ? (
                    // App configured but not installed
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-800 text-gray-100">
                        GitHub App Required
                      </span>
                      <div className="flex items-start gap-2 text-xs text-gray-600">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>Install our GitHub App on your organization to enable compliance scanning.</span>
                      </div>
                    </>
                  ) : (
                    // App not configured
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-800 text-gray-100">
                        GitHub App Required
                      </span>
                      <div className="flex items-start gap-2 text-xs text-amber-600">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>GitHub App not configured. Contact your administrator.</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 w-full sm:w-auto sm:ml-4 sm:flex-shrink-0">
            <button
              onClick={handleConnectClick}
              disabled={disabled || isConnecting || (isGitHub && !githubAppConfigured)}
              className={`
                px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2
                ${effectiveConnected
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-teal-600 text-white hover:bg-teal-700'
                }
                ${(disabled || isConnecting || (isGitHub && !githubAppConfigured)) ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {getConnectButtonText()}
              {isGitHub && <ExternalLink className="w-4 h-4" />}
            </button>

            {isGitHub && hasGitHubInstallations && onRemoveInstallation && (
              <button
                onClick={onRemoveInstallation}
                disabled={disabled}
                className="px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors text-red-600 hover:bg-red-50 border border-red-200"
              >
                Remove Installation
              </button>
            )}

            {/* Select Role button for connected AWS SSO integrations */}
            {isAWS && connected && onConfigureRole && (
              <button
                onClick={() => onConfigureRole(integrationId)}
                disabled={disabled}
                className="px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors bg-orange-100 text-orange-700 hover:bg-orange-200"
              >
                Select AWS Role
              </button>
            )}

            {!effectiveConnected && onSkip && (
              <button
                onClick={onSkip}
                disabled={disabled}
                className="px-4 py-2 min-h-[44px] text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip for now
              </button>
            )}
          </div>
        </div>

        {/* Skip Warning */}
        {!effectiveConnected && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              Skipping this integration means related controls will require manual verification during your audit.
            </p>
          </div>
        )}
      </div>

      {/* AWS SSO Configuration Modal */}
      {isAWS && (
        <AWSSSOModal
          isOpen={showSSOModal}
          onClose={() => setShowSSOModal(false)}
          onSubmit={handleSSOSubmit}
          isLoading={isConnecting}
        />
      )}

      {/* AWS SSO Verification Modal */}
      {showVerificationModal && (
        <AWSSSOVerificationModal
          isOpen={true}
          verificationUrl={ssoVerificationUrl}
          userCode={ssoUserCode}
          status={ssoStatus}
          message={ssoMessage}
          onCancel={onCancelSSO}
        />
      )}

      {/* AWS SSO Role Selection Modal */}
      {isAWS && ssoShowRoleSelection && (
        <AWSSSOSelectRoleModal
          isOpen={true}
          accounts={ssoAccounts || []}
          loading={ssoAccountsLoading}
          error={ssoAccountsError}
          message={ssoMessage}
          onSelectRole={onSelectSSORole}
          onCancel={onCancelSSO}
        />
      )}
    </>
  );
};

export default IntegrationCard;
